<template>
  <section :class="$style.format">
    <div :class="$style.imageWrapper">
      <BasePicture
        :src="`${data?.fields?.image?.fields?.file?.url}?w=912&h=450&fit=fill&q=70`"
        :img-class="$style.image"
        :class="$style.picture"
        alt=""
        aria-hidden="true"
      />
      <BaseHeading size="h3" :class="$style.name">
        {{ data?.fields?.name }}
      </BaseHeading>
    </div>
    <div :class="$style.info">
      <BaseHeading size="h5" :class="$style.heading">
        {{ data?.fields?.heading }}
      </BaseHeading>
      <MarkedParser :class="$style.copy" :md="data?.fields?.copy" />
    </div>
    <div :class="$style.details">
      <FormatDetail
        v-for="detail in computedDetails"
        :key="detail.heading"
        :data="detail"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { TypeFormatWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { localizedStrings } = useLocalizedStrings()

// Props
const props = defineProps<{
  data: TypeFormatWithoutUnresolvableLinksResponse
  icons: {
    rules: string
    usage: string
    deckSize: string
    sideboard: string
    cardLimit: string
    banned: string
  }
}>()

// Computed
const computedDetails = computed(() => {
  const ls = localizedStrings.value
  return [
    {
      icon: props?.icons?.rules,
      heading: ls['rules'],
      copy: props?.data?.fields?.rulesCopy || '',
    },
    {
      icon: props?.icons?.usage,
      heading: ls['usage'],
      copy: props?.data?.fields?.usageCopy || '',
    },
    {
      icon: props?.icons?.deckSize,
      heading: ls['deck-size'],
      copy: props?.data?.fields?.deckSizeCopy || '',
    },
    {
      icon: props?.icons?.sideboard,
      heading: ls['sideboard'],
      copy: props?.data?.fields?.sideboardCopy || '',
    },
    {
      icon: props?.icons?.cardLimit,
      heading: `${props?.data?.fields?.cardLimit || ''} ${ls['card-limit']}`,
      copy: props?.data?.fields?.cardLimitCopy || '',
    },
    {
      icon: props?.icons?.banned,
      heading: ls['banned-list'],
      copy: props?.data?.fields?.bannedListCopy || '',
    },
  ]
})
</script>

<style lang="scss" module>
.imageWrapper {
  height: 250px;
  margin-bottom: 18px;
  position: relative;

  &:after {
    background-image: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0) 40%
    );
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  @include media('sm') {
    height: 450px;
    margin-bottom: 36px;
  }
}

.picture {
  height: 100%;
}

.image {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.name {
  bottom: 20px;
  color: var(--white);
  left: 28px;
  position: absolute;
  z-index: 1;

  @include media('md') {
    bottom: 40px;
    left: 48px;
  }
}

.info {
  margin-bottom: 20px;
  padding: 0 30px;

  @include media('md') {
    margin-bottom: 45px;
    padding: 0;
  }
}

.heading {
  color: var(--black);
  margin-bottom: 10px;
}

.copy {
  color: var(--gray-8);
  font-size: to-rem(19px);
  line-height: math.div(30, 19);

  p {
    margin-bottom: 20px;
  }
}

.details {
  background-color: var(--gray-3);
  border: 1px solid var(--gray-3);
  box-shadow: 0 4px 13px 0 var(--black-alpha10);
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(1, 1fr);
  margin: 0 30px 30px;

  @include media('md') {
    grid-template-columns: repeat(2, 1fr);
    margin: 0;
  }
}
</style>
